import {
  Dashboard,
  DashboardCreate,
  DashboardDefaultUpdate,
  DashboardListResponse,
  DashboardUpdate,
  Dashlet,
  DashletCreate,
  DashletData,
  SyndicatedDashboard,
} from "types";
import {
  DeleteDashboardDashletArgs,
  GetAllDashboardsParams,
  UpdateDashboardDashletsBulkParams,
  UpdateDashboardDashletsGlobalFiltersBulkParams,
} from "api/services/DashboardsAPI";
import {
  QueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { DashboardsAPI } from "api";
import { replaceDashlet } from "utils";

const queryKeys = {
  all: () => ["dashboards"] as const,
  list: () => [...queryKeys.all(), "list"],
  syndicated: () => [...queryKeys.all(), "syndicated"] as const,
  details: () => [...queryKeys.all(), "details"] as const,
  detail: (uuid: string) => [...queryKeys.details(), uuid] as const,
  dashlets: (uuid: string) => [...queryKeys.detail(uuid), "dashlets"] as const,
};

/**
 * Appends a new Dashboard to the cached list of Dashboards.
 * Useful after an add/clone mutation to eagerly update the local cache.
 */
const appendNewDashboardToCache = (queryClient: QueryClient, newDashboard: Dashboard): void => {
  queryClient.setQueryData<DashboardListResponse>(
    queryKeys.list(),
    (oldData = { defaultUuid: null, dashboards: [] }) => ({
      ...oldData,
      dashboards: [...oldData.dashboards, newDashboard],
    }),
  );
};

/**
 * Updates an existing Dashboard in the detail cache of Dashboards.
 * Useful after an edit mutation to eagerly update the local cache.
 */
const updateDashboardInDetailCache = (queryClient: QueryClient, dashboard: Dashboard): void => {
  queryClient.setQueryData<Dashboard>(queryKeys.detail(dashboard.uuid), (oldData) => ({ ...oldData, ...dashboard }));
};

/**
 * Invalidates the list query to ensure it loads fresh when used next.
 */
const invalidateListQuery = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: queryKeys.list() });

/**
 * Replaces the dashlets in cache for a given dashboard.
 * We use this after dashlet re-order to update the entire list.
 *
 * Normally we would do this on success of the mutation itself.
 * The re-order code works a little differently though so this function is exposed
 * to allow the component to call this as needed.
 */
export const replaceDashletsInCache = (queryClient: QueryClient, dashboardId: string, newDashlets: Dashlet[]) =>
  queryClient.setQueriesData<Dashlet[]>(queryKeys.dashlets(dashboardId), () => newDashlets);

/**
 * Fetches all Dashboards for the current user.
 *
 * By default we set the `staleTime` to 5 seconds (you can always override).
 * This supports for the use case where a user hits the root `/dashboard` route
 * and then we immediately redirect them to `/dashboard/:uuid`.
 * Both of those will make a call to load the list of dashboards and a small staleTime
 * is intended to remove the need for making the actual network request twice.
 */
export const useDashboards = <T = DashboardListResponse>(
  params: GetAllDashboardsParams,
  options: UseQueryOptions<DashboardListResponse, unknown, T> = {},
) =>
  useQuery<DashboardListResponse, unknown, T>(queryKeys.list(), () => DashboardsAPI.getAll(params), {
    staleTime: 1000 * 5,
    ...options,
  });

/**
 * Fetches the list of syndicated dashboards.
 */
export const useSyndicatedDashboards = <T = SyndicatedDashboard[]>(
  options: UseQueryOptions<SyndicatedDashboard[], unknown, T> = {},
) => useQuery<SyndicatedDashboard[], unknown, T>(queryKeys.syndicated(), () => DashboardsAPI.getSyndicated(), options);

/**
 * Fetches a Dashboard instance by id.
 */
export const useDashboard = <T = Dashboard>(uuid: string, options: UseQueryOptions<Dashboard, unknown, T> = {}) =>
  useQuery<Dashboard, unknown, T>(queryKeys.detail(uuid), () => DashboardsAPI.get(uuid), options);

/**
 * Mutation to create a new Dashboard.
 *
 * NOTE: This automatically updates the cache on success.
 */
export const useCreateDashboard = (options: UseMutationOptions<Dashboard, unknown, DashboardCreate> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((data) => DashboardsAPI.create(data), {
    ...options,
    onSuccess(data, variables, context) {
      appendNewDashboardToCache(queryClient, data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};
/**
 * Mutation to update a Dashboard.
 *
 * NOTE: This automatically updates the cache on success.
 */
export const useUpdateDashboard = (
  uuid: string,
  options: UseMutationOptions<Dashboard, unknown, DashboardUpdate> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((data) => DashboardsAPI.update(uuid, data), {
    ...options,
    onSuccess(data, variables, context) {
      invalidateListQuery(queryClient);
      updateDashboardInDetailCache(queryClient, data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to clone an existing Dashboard.
 *
 * NOTE: This automatically updates the cache on success.
 */
export const useCloneDashboard = (
  uuid: string,
  options: UseMutationOptions<Dashboard, unknown, DashboardCreate> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((data) => DashboardsAPI.clone(uuid, data), {
    ...options,
    onSuccess(data, variables, context) {
      appendNewDashboardToCache(queryClient, data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to update a default Dashboard.
 *
 * Note this automatically updates the cache on success.
 */
export const useUpdateDefaultDashboard = (
  uuid: string,
  options: UseMutationOptions<void, unknown, DashboardDefaultUpdate> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((request) => DashboardsAPI.updateDefault(uuid, request), {
    ...options,
    onSuccess(data, variables, context) {
      invalidateListQuery(queryClient);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to delete a Dashboard.
 *
 * Note this automatically updates the cache on success.
 */
export const useDeleteDashboard = (uuid: string, options: UseMutationOptions<void, unknown, void> = {}) => {
  const queryClient = useQueryClient();
  return useMutation(() => DashboardsAPI.delete(uuid), {
    ...options,
    onSuccess(data, variables, context) {
      invalidateListQuery(queryClient);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to export a Dashboard.
 */
export const useExportDashboard = (uuid: string, options: UseMutationOptions<string, unknown, DashletData[]> = {}) =>
  useMutation((dashletData) => DashboardsAPI.export(uuid, dashletData), options);

/**
 * Fetches all dashlets for a dashboard instance by id.
 */
export const useDashboardDashlets = <T = Dashlet[]>(
  uuid: string,
  options: UseQueryOptions<Dashlet[], unknown, T> = {},
) => useQuery<Dashlet[], unknown, T>(queryKeys.dashlets(uuid), () => DashboardsAPI.getDashlets(uuid), options);

/**
 * Mutation to create a Dashlet and add it to a Dashboard.
 */
export const useAddDashletToDashboard = (options: UseMutationOptions<Dashlet, unknown, DashletCreate> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((createData) => DashboardsAPI.createDashboardDashlet(createData), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.setQueriesData<Dashlet[]>(queryKeys.dashlets(data.dashboardUuid), (cachedData = []) => [
        ...cachedData,
        data,
      ]);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to update a dashlet.
 *
 * Note this automatically updates the cache on success.
 */
export const useUpdateDashlet = (options: UseMutationOptions<Dashlet, unknown, Dashlet> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((args) => DashboardsAPI.updateDashboardDashlet(args), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.setQueriesData<Dashlet[]>(queryKeys.dashlets(variables.dashboardUuid), (cachedData = []) =>
        replaceDashlet(variables, cachedData),
      );
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to update a dashlet.
 *
 * Note this automatically updates the cache on success.
 */
export const useCloneDashlet = (options: UseMutationOptions<Dashlet, unknown, Dashlet> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((args) => DashboardsAPI.cloneDashboardDashlet(args), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.setQueriesData<Dashlet[]>(queryKeys.dashlets(variables.dashboardUuid), (cachedData = []) => [
        ...cachedData,
        data,
      ]);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to update dashlet order in bulk.
 */
export const useUpdateDashletBulk = (
  options: UseMutationOptions<Dashlet[], unknown, UpdateDashboardDashletsBulkParams> = {},
) => {
  return useMutation((args) => DashboardsAPI.updateDashboardDashletsBulk(args), options);
};

/**
 * Mutation to delete a dashlet from a Dashboard.
 *
 * Note this automatically updates the cache on success.
 */
export const useDeleteDashlet = (options: UseMutationOptions<void, unknown, DeleteDashboardDashletArgs> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((args) => DashboardsAPI.deleteDashboardDashlet(args), {
    ...options,
    onSuccess(data, variables, context) {
      queryClient.setQueriesData<Dashlet[]>(queryKeys.dashlets(variables.dashboardUuid), (cachedData = []) =>
        cachedData.filter((x) => x.dashletUuid !== variables.dashletUuid),
      );
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to update dashlet filters in bulk.
 *
 * Note this automatically updates the cache on success.
 */
export const useUpdateDashletGlobalFiltersBulk = (
  options: UseMutationOptions<Dashlet[], unknown, UpdateDashboardDashletsGlobalFiltersBulkParams> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation((args) => DashboardsAPI.updateDashboardDashletsGlobalFiltersBulk(args), {
    ...options,
    onSuccess(data, variables, context) {
      replaceDashletsInCache(queryClient, variables.dashboardUuid, data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};
